import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles, Loader2, CheckCircle, XCircle, RefreshCw, Send, Image as ImageIcon,
  Clock, AlertTriangle, Facebook, Wand2, Lightbulb, Settings2, ArrowRight, Video, Calendar,
  Wallet, Plus, History, ChevronDown, ChevronUp, Mail, Trash2, Download,
} from 'lucide-react';
import { API_URL } from '../../config';
import TeckiaCMWizardModal from './TeckiaCMWizardModal';
import TeckiaCMStudioModal from './TeckiaCMStudioModal';
import StripePaymentForm from '../client/StripePaymentForm';

const MVOLA_NUMBER = '034 26 126 52';
const MVOLA_ACCOUNT_NAME = 'TDI DIGITAL';
const ORANGE_NUMBER = '037 88 985 79';
const ORANGE_ACCOUNT_NAME = 'HERY NY AINA MIADANTSOA Nomenjanahary';

const MEDIA_RETENTION_DAYS = 14;
const daysRemaining = (createdAt) => {
  if (!createdAt) return null;
  const ageMs = Date.now() - new Date(createdAt).getTime();
  return MEDIA_RETENTION_DAYS - Math.floor(ageMs / 86400000);
};

const ORDER_STATUS_LABELS = {
  pending: { label: 'En attente de vérification', color: 'text-amber-300 bg-amber-500/10' },
  validated: { label: 'Validé', color: 'text-green-300 bg-green-500/10' },
  rejected: { label: 'Rejeté', color: 'text-red-300 bg-red-500/10' },
};

// Publication automatique/configurable (wizard + planning + frequence) - pas encore
// proposee aux clients, seul TeckiA CM Studio (manuel) est actif pour le moment.
// Remettre a true pour la reactiver quand elle sera prete.
const AUTOMATIC_CM_ENABLED = false;

// ============================================================
// COMMUNITY MANAGER IA - Publication automatique sur la Page
// Genere legende + image, publie apres validation (ou auto selon config).
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const STATUS_LABELS = {
  draft_review: { label: 'Brouillon — à vérifier', color: 'text-purple-300 bg-purple-500/10' },
  generating: { label: 'Génération en cours...', color: 'text-blue-300 bg-blue-500/10' },
  pending_validation: { label: 'À valider', color: 'text-yellow-300 bg-yellow-500/10' },
  scheduled: { label: 'Planifiée', color: 'text-blue-300 bg-blue-500/10' },
  published: { label: 'Publié', color: 'text-green-300 bg-green-500/10' },
  rejected: { label: 'Rejeté', color: 'text-gray-400 bg-gray-500/10' },
  failed: { label: 'Échec', color: 'text-red-300 bg-red-500/10' },
};

export default function CommunityManagerPanel({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [cfg, setCfg] = useState({
    enabled: false, frequency_value: 3, frequency_unit: 'days', validation_mode: 'manual',
    content_brief: '', media_type: 'image', activity_type: '', activity_detail: '',
    page_info_json: '', strategy_proposal: '', content_plan_images: 1, content_plan_videos: 0,
    image_style: 'beautified', video_style: 'ai_ad', language: 'fr', repeat_tomorrow: false,
    wizard_completed: false, text_brief_mode: 'text',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [videoCount, setVideoCount] = useState(0);
  const [maxStoredVideos, setMaxStoredVideos] = useState(25);
  const [activityTypes, setActivityTypes] = useState([]);
  const [fetchingPageInfo, setFetchingPageInfo] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showStudio, setShowStudio] = useState(false);
  const [pageName, setPageName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [editCaptions, setEditCaptions] = useState({});
  const [editImagePrompts, setEditImagePrompts] = useState({});
  const [editScripts, setEditScripts] = useState({});
  const [actingOn, setActingOn] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupMethod, setTopupMethod] = useState('mvola');
  const [topupNote, setTopupNote] = useState('');
  const [toppingUp, setToppingUp] = useState(false);
  const [topupMessage, setTopupMessage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeMethod, setRechargeMethod] = useState('mvola');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeRef, setRechargeRef] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderMessage, setOrderMessage] = useState(null);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const pollRef = useRef(null);

  const loadConfig = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/cm/config/${clientId}`, { headers: authHeaders() });
    const data = await res.json();
    if (data.success) {
      setCfg(data.config);
      setCanEdit(data.can_edit);
      setPageName(data.page_name || '');
      setIsAdmin(!!data.is_admin);
      setCreditBalance(data.credit_balance || 0);
    }
  }, [clientId]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/credit-transactions`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setTransactions(data.transactions);
    } catch (e) { console.error(e); }
    finally { setLoadingHistory(false); }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadHistory();
  };

  const submitTopup = async () => {
    const amount = parseInt(topupAmount, 10);
    if (!amount || amount <= 0) {
      setTopupMessage({ type: 'error', text: 'Montant invalide.' });
      return;
    }
    setToppingUp(true);
    setTopupMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/credit-topup`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ amount, method: topupMethod, note: topupNote.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setCreditBalance(data.balance);
        setTopupAmount('');
        setTopupNote('');
        setTopupMessage({ type: 'ok', text: `Solde mis à jour : ${data.balance.toLocaleString('fr-FR')} Ar` });
        if (showHistory) loadHistory();
      } else {
        setTopupMessage({ type: 'error', text: data.message });
      }
    } catch {
      setTopupMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setToppingUp(false); }
  };

  const loadMyOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/credit-orders`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMyOrders(data.orders);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (!isAdmin) loadMyOrders(); }, [isAdmin, clientId]);

  const submitOrder = async () => {
    const amount = parseInt(rechargeAmount, 10);
    if (!amount || amount <= 0) {
      setOrderMessage({ type: 'error', text: 'Montant invalide.' });
      return;
    }
    if (!rechargeRef.trim()) {
      setOrderMessage({ type: 'error', text: 'La référence de paiement est requise.' });
      return;
    }
    setSubmittingOrder(true);
    setOrderMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/credit-order`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ amount, method: rechargeMethod, payment_ref: rechargeRef.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setOrderMessage({ type: 'ok', text: "Déclaration envoyée ! Notre équipe vérifie votre paiement et crédite votre compte sous peu." });
        setRechargeAmount('');
        setRechargeRef('');
        loadMyOrders();
      } else {
        setOrderMessage({ type: 'error', text: data.message });
      }
    } catch {
      setOrderMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setSubmittingOrder(false); }
  };

  const onStripeSuccess = () => {
    setShowStripeForm(false);
    setShowRecharge(false);
    setOrderMessage({ type: 'ok', text: 'Paiement reçu ! Votre solde est mis à jour.' });
    loadConfig();
  };

  const loadPosts = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/cm/posts/${clientId}`, { headers: authHeaders() });
    const data = await res.json();
    if (data.success) {
      setPosts(data.posts);
      setVideoCount(data.video_count || 0);
      setMaxStoredVideos(data.max_stored_videos || 25);
    }
    return data.posts || [];
  }, [clientId]);

  const deletePost = async (postId) => {
    if (!window.confirm('Supprimer définitivement cette publication et son média du serveur ? Cette action est irréversible.')) return;
    setActingOn(postId);
    try {
      await fetch(`${API_URL}/api/cm/posts/${clientId}/${postId}`, { method: 'DELETE', headers: authHeaders() });
      await loadPosts();
    } finally { setActingOn(null); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadConfig(), loadPosts()]);
      setLoading(false);
      try {
        const res = await fetch(`${API_URL}/api/cm/activity-types`, { headers: authHeaders() });
        const data = await res.json();
        if (data.success) setActivityTypes(data.types);
      } catch (e) { console.error(e); }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadConfig, loadPosts]);

  const fetchPageInfo = async () => {
    setFetchingPageInfo(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/fetch-page-info`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setCfg(prev => ({ ...prev, page_info_json: JSON.stringify(data.info) }));
        setMessage(data.partial
          ? { type: 'info', text: data.note }
          : { type: 'ok', text: `Infos récupérées : ${data.info.name || 'Page'}` });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setFetchingPageInfo(false); }
  };

  const proposeStrategy = async () => {
    setProposing(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/propose-strategy`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ activity_type: cfg.activity_type, activity_detail: cfg.activity_detail })
      });
      const data = await res.json();
      if (data.success) {
        setCfg(prev => ({ ...prev, strategy_proposal: data.proposal }));
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setProposing(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(cfg)
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.success ? 'Enregistré !' : data.message });
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setSaving(false); }
  };

  const generateNow = async () => {
    setGenerating(true);
    setMessage(null);
    try {
      // La legende/description est generee tout de suite (LLM synchrone) : le post revient
      // en statut 'draft_review', a vous de verifier/corriger avant de lancer l'image/video.
      const res = await fetch(`${API_URL}/api/cm/generate/${clientId}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) {
        setMessage({ type: 'error', text: data.message });
        setGenerating(false);
        return;
      }
      await loadPosts();
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally { setGenerating(false); }
  };

  const confirmDraft = async (postId) => {
    setActingOn(postId);
    try {
      const res = await fetch(`${API_URL}/api/cm/posts/${clientId}/${postId}/confirm-draft`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ caption: editCaptions[postId], image_prompt: editImagePrompts[postId], video_script: editScripts[postId] })
      });
      const data = await res.json();
      if (!data.success) {
        setMessage({ type: 'error', text: data.message });
        setActingOn(null);
        return;
      }
      await loadPosts();
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        const list = await loadPosts();
        const p = list.find(x => x.id === postId);
        if (!p || p.status !== 'generating' || ticks > 30) {
          clearInterval(pollRef.current);
          setActingOn(null);
        }
      }, 3000);
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur' });
      setActingOn(null);
    }
  };

  const approve = async (postId) => {
    setActingOn(postId);
    try {
      const res = await fetch(`${API_URL}/api/cm/posts/${clientId}/${postId}/approve`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ caption: editCaptions[postId] })
      });
      const data = await res.json();
      if (!data.success) setMessage({ type: 'error', text: data.message });
      await loadPosts();
    } finally { setActingOn(null); }
  };

  const reject = async (postId) => {
    setActingOn(postId);
    try {
      await fetch(`${API_URL}/api/cm/posts/${clientId}/${postId}/reject`, { method: 'POST', headers: authHeaders() });
      await loadPosts();
    } finally { setActingOn(null); }
  };

  if (loading) {
    return <div className="py-8 text-center"><Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto" /></div>;
  }

  if (!canEdit) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm bg-black/20 rounded-xl p-4">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        Ce module est géré par l'équipe TDI DIGITAL pour votre Page.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-white font-medium">
        <Sparkles className="w-4 h-4 text-purple-400" /> TeckiA CM Studio
        <span className="text-[10px] text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded-full">Bêta</span>
      </div>
      <p className="text-gray-400 text-xs -mt-3">
        TeckiA CM Studio génère du contenu et publie sur votre Page — automatiquement, manuellement, ou après votre validation.
      </p>

      {(() => {
        const low = creditBalance <= 0;
        const tight = !low && creditBalance < 5000;
        const tone = low
          ? { ring: 'border-red-500/30', bg: 'from-red-600/15 via-rose-600/10 to-transparent', num: 'text-red-300', icon: 'text-red-300 bg-red-500/15' }
          : tight
          ? { ring: 'border-amber-500/30', bg: 'from-amber-500/15 via-orange-500/10 to-transparent', num: 'text-amber-300', icon: 'text-amber-300 bg-amber-500/15' }
          : { ring: 'border-emerald-500/25', bg: 'from-emerald-500/15 via-teal-500/10 to-transparent', num: 'text-emerald-300', icon: 'text-emerald-300 bg-emerald-500/15' };
        return (
          <div className={`rounded-2xl border ${tone.ring} bg-gradient-to-br ${tone.bg} p-4 space-y-3`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tone.icon}`}>
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-gray-400 text-[11px] uppercase tracking-wide">Solde TeckiA CM Studio</div>
                  <div className={`text-xl font-bold ${tone.num}`}>{creditBalance.toLocaleString('fr-FR')} Ar</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin ? (
                  <button
                    onClick={() => { setShowTopup(v => !v); setTopupMessage(null); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" /> Ajouter un paiement
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowRecharge(v => !v); setOrderMessage(null); setShowStripeForm(false); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-semibold"
                  >
                    <Plus className="w-3.5 h-3.5" /> Recharger mon compte
                  </button>
                )}
                <button
                  onClick={toggleHistory}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/20 hover:bg-black/30 text-gray-300 text-xs font-medium"
                >
                  <History className="w-3.5 h-3.5" /> Historique
                  {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {!isAdmin && (low || tight) && (
              <div className={`flex items-start gap-2 text-xs rounded-lg p-2.5 ${low ? 'bg-red-500/10 text-red-200' : 'bg-amber-500/10 text-amber-200'}`}>
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  {low ? 'Solde insuffisant pour générer du contenu.' : 'Votre solde est bas.'}{' '}
                  Cliquez sur « Recharger mon compte » ci-dessus, ou contactez-nous —{' '}
                  <a href="mailto:contact@tdidigital.io" className="underline inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> contact@tdidigital.io
                  </a>
                </span>
              </div>
            )}

            {!isAdmin && myOrders.some(o => o.status === 'pending') && (
              <div className="flex items-start gap-2 text-xs rounded-lg p-2.5 bg-blue-500/10 text-blue-200">
                <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  {myOrders.filter(o => o.status === 'pending').length} déclaration(s) de paiement en attente de vérification par notre équipe.
                </span>
              </div>
            )}

            {isAdmin && showTopup && (
              <div className="bg-black/20 rounded-xl p-3 space-y-2 border border-white/10">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number" min={1} placeholder="Montant (Ar)"
                    value={topupAmount}
                    onChange={e => setTopupAmount(e.target.value)}
                    className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                  />
                  <select
                    value={topupMethod}
                    onChange={e => setTopupMethod(e.target.value)}
                    className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="mvola">Mvola</option>
                    <option value="orange">Orange Money</option>
                    <option value="card">Carte bancaire</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                <input
                  type="text" placeholder="Référence / note (optionnel)"
                  value={topupNote}
                  onChange={e => setTopupNote(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                />
                <div className="flex items-center gap-3">
                  <button
                    onClick={submitTopup}
                    disabled={toppingUp}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium disabled:opacity-50"
                  >
                    {toppingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Valider la recharge
                  </button>
                  {topupMessage && (
                    <span className={`text-xs ${topupMessage.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{topupMessage.text}</span>
                  )}
                </div>
              </div>
            )}

            {!isAdmin && showRecharge && (
              <div className="bg-black/20 rounded-xl p-3 space-y-3 border border-white/10">
                <div className="flex gap-2">
                  {[
                    { id: 'mvola', label: 'Mvola' },
                    { id: 'orange', label: 'Orange Money' },
                    { id: 'card', label: 'Carte bancaire' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => { setRechargeMethod(opt.id); setShowStripeForm(false); setOrderMessage(null); }}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        rechargeMethod === opt.id ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-black/20 border-white/10 text-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {rechargeMethod !== 'card' ? (
                  <>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-xs text-emerald-100">
                      Envoyez votre paiement au numéro{' '}
                      <span className="font-semibold">{rechargeMethod === 'mvola' ? MVOLA_NUMBER : ORANGE_NUMBER}</span>{' '}
                      au nom de <span className="font-semibold">{rechargeMethod === 'mvola' ? MVOLA_ACCOUNT_NAME : ORANGE_ACCOUNT_NAME}</span>,
                      puis indiquez ci-dessous le montant envoyé et la référence de la transaction (reçue par SMS).
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number" min={1} placeholder="Montant envoyé (Ar)"
                        value={rechargeAmount}
                        onChange={e => setRechargeAmount(e.target.value)}
                        className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                      />
                      <input
                        type="text" placeholder="Référence de la transaction"
                        value={rechargeRef}
                        onChange={e => setRechargeRef(e.target.value)}
                        className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={submitOrder}
                        disabled={submittingOrder}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium disabled:opacity-50"
                      >
                        {submittingOrder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Déclarer mon paiement
                      </button>
                      {orderMessage && (
                        <span className={`text-xs ${orderMessage.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{orderMessage.text}</span>
                      )}
                    </div>
                  </>
                ) : showStripeForm ? (
                  <StripePaymentForm
                    userId={clientId}
                    cart={[{ service: 'cm_studio', creditType: 'ariary', quota: parseInt(rechargeAmount, 10) || 0, quantity: 1 }]}
                    getTotalAmount={() => parseInt(rechargeAmount, 10) || 0}
                    onSuccess={onStripeSuccess}
                    onCancel={() => setShowStripeForm(false)}
                  />
                ) : (
                  <>
                    <input
                      type="number" min={2500} placeholder="Montant à recharger (Ar, min. 2 500)"
                      value={rechargeAmount}
                      onChange={e => setRechargeAmount(e.target.value)}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500"
                    />
                    <button
                      onClick={() => {
                        const amount = parseInt(rechargeAmount, 10);
                        if (!amount || amount < 2500) { setOrderMessage({ type: 'error', text: 'Montant minimum : 2 500 Ar.' }); return; }
                        setOrderMessage(null);
                        setShowStripeForm(true);
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium"
                    >
                      <ArrowRight className="w-3.5 h-3.5" /> Continuer vers le paiement
                    </button>
                    {orderMessage && (
                      <span className="text-xs text-red-400 block">{orderMessage.text}</span>
                    )}
                  </>
                )}
              </div>
            )}

            {!isAdmin && myOrders.length > 0 && (
              <div className="space-y-1.5">
                {myOrders.slice(0, 5).map(o => {
                  const s = ORDER_STATUS_LABELS[o.status] || ORDER_STATUS_LABELS.pending;
                  return (
                    <div key={o.id} className="flex items-center justify-between text-xs bg-black/15 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.color}`}>{s.label}</span>
                        <span className="text-gray-500 ml-1.5">({o.method})</span>
                        <div className="text-gray-500 text-[10px] mt-0.5">{new Date(o.created_at).toLocaleString('fr-FR')}</div>
                      </div>
                      <span className="font-medium text-gray-300 flex-shrink-0 ml-2">{o.amount_ar.toLocaleString('fr-FR')} Ar</span>
                    </div>
                  );
                })}
              </div>
            )}

            {showHistory && (
              <div className="space-y-1.5">
                {loadingHistory ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin mx-auto" />
                ) : transactions.length === 0 ? (
                  <p className="text-gray-500 text-xs text-center py-2">Aucun mouvement pour l'instant.</p>
                ) : (
                  transactions.map(t => (
                    <div key={t.id} className="flex items-center justify-between text-xs bg-black/15 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <span className="text-gray-300">
                          {t.type === 'topup' ? 'Recharge' : t.type === 'refund' ? 'Remboursement' : 'Génération'}
                        </span>
                        {t.method && <span className="text-gray-500 ml-1.5">({t.method})</span>}
                        {t.note && <span className="text-gray-500 ml-1.5 truncate">— {t.note}</span>}
                        <div className="text-gray-500 text-[10px]">{new Date(t.created_at).toLocaleString('fr-FR')}</div>
                      </div>
                      <span className={`font-medium flex-shrink-0 ml-2 ${t.amount >= 0 ? 'text-emerald-400' : 'text-gray-300'}`}>
                        {t.amount >= 0 ? '+' : ''}{t.amount.toLocaleString('fr-FR')} Ar
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })()}

      <button
        onClick={() => setShowStudio(true)}
        className="w-full relative overflow-hidden flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-fuchsia-600/25 via-purple-600/25 to-blue-600/25 border border-purple-400/40 hover:border-purple-300/60 transition-all text-left group"
      >
        <div className="absolute -inset-1 bg-gradient-to-r from-fuchsia-500/0 via-purple-400/10 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 via-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-900/40 animate-pulse">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-semibold flex items-center gap-2">
            Ouvrir TeckiA CM Studio
            <span className="text-[9px] text-fuchsia-200 bg-fuchsia-500/15 px-1.5 py-0.5 rounded-full">Nouveau</span>
          </div>
          <div className="text-gray-400 text-xs">Créez vous-même votre publication : photos, prompt libre ou bibliothèque, IA au choix</div>
        </div>
        <ArrowRight className="w-4 h-4 text-purple-300 flex-shrink-0" />
      </button>

      {showStudio && (
        <TeckiaCMStudioModal
          clientId={clientId}
          cfg={cfg}
          creditBalance={creditBalance}
          onClose={() => { setShowStudio(false); loadConfig(); }}
          onPublished={() => { loadPosts(); loadConfig(); }}
        />
      )}

      {AUTOMATIC_CM_ENABLED && (
      <>
      <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-white text-sm font-medium">
          <Wand2 className="w-4 h-4 text-purple-300" /> Configuration guidée par TeckiA CM Studio
        </div>
        <p className="text-gray-400 text-xs -mt-2">
          Aide à démarrer : récupère les infos publiques de votre Page, puis propose une stratégie de publication adaptée à votre activité.
        </p>

        <button
          onClick={fetchPageInfo}
          disabled={fetchingPageInfo}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600/80 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50"
        >
          {fetchingPageInfo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Facebook className="w-3.5 h-3.5" />}
          Récupérer les infos de ma Page
        </button>
        {cfg.page_info_json && (() => {
          let info = {};
          try { info = JSON.parse(cfg.page_info_json); } catch { /* ignore */ }
          return (
            <div className="bg-black/20 rounded-lg p-2.5 text-[11px] text-gray-300 space-y-0.5">
              {info.name && <div><span className="text-gray-500">Nom :</span> {info.name}</div>}
              {info.category && <div><span className="text-gray-500">Catégorie :</span> {info.category}</div>}
              {(info.about || info.description) && <div><span className="text-gray-500">Bio :</span> {info.about || info.description}</div>}
              {info.phone && <div><span className="text-gray-500">Tél :</span> {info.phone}</div>}
              {typeof info.fan_count === 'number' && <div><span className="text-gray-500">Abonnés :</span> {info.fan_count}</div>}
            </div>
          );
        })()}

        <div>
          <label className="text-gray-400 text-xs">Quelle est l'activité de cette Page ?</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {activityTypes.map(t => (
              <button
                key={t.id}
                onClick={() => setCfg(prev => ({ ...prev, activity_type: t.id }))}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
                  cfg.activity_type === t.id ? 'bg-purple-600 border-purple-500 text-white' : 'bg-black/20 border-white/10 text-gray-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={cfg.activity_detail}
            onChange={e => setCfg(prev => ({ ...prev, activity_detail: e.target.value }))}
            placeholder="Précisez si besoin (ex: vente de vêtements pour enfants...)"
            className="w-full mt-2 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
          />
        </div>

        <button
          onClick={proposeStrategy}
          disabled={proposing || !cfg.activity_type}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium disabled:opacity-50"
        >
          {proposing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}
          Proposer une stratégie
        </button>
        {cfg.strategy_proposal && (
          <div className="bg-black/20 rounded-lg p-3 text-xs text-gray-200 whitespace-pre-wrap border border-purple-500/20">
            {cfg.strategy_proposal}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowWizard(true)}
        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 hover:border-purple-400/50 transition-all text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
          <Settings2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-semibold">Configurer la publication automatique</div>
          <div className="text-gray-400 text-xs">
            {cfg.wizard_completed
              ? `Plan actuel : ${cfg.content_plan_images || 0} image(s), ${cfg.content_plan_videos || 0} vidéo(s) — ${cfg.language === 'mg' ? 'malagasy' : cfg.language === 'en' ? 'anglais' : 'français'}`
              : 'Répondez à quelques questions pour préparer votre prochaine publication'}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-purple-300 flex-shrink-0" />
      </button>

      {showWizard && (
        <TeckiaCMWizardModal
          clientId={clientId}
          cfg={cfg}
          pageName={pageName}
          pageInfo={(() => { try { return JSON.parse(cfg.page_info_json || '{}'); } catch { return {}; } })()}
          activityLabel={activityTypes.find(t => t.id === cfg.activity_type)?.label}
          onClose={() => setShowWizard(false)}
          onSaved={(answers) => setCfg(prev => ({ ...prev, ...answers, wizard_completed: true }))}
        />
      )}

      <div className="bg-black/20 rounded-xl p-4 space-y-3">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-white text-sm">Activer la publication automatique</span>
          <button
            onClick={() => setCfg(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`relative w-10 h-6 rounded-full transition-all ${cfg.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.enabled ? 'left-4.5' : 'left-0.5'}`} style={cfg.enabled ? { left: '1.125rem' } : {}} />
          </button>
        </label>

        <div>
          <label className="text-gray-400 text-xs">Fréquence : une publication tous les</label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number" min={1} max={cfg.frequency_unit === 'hours' ? 72 : 30}
              value={cfg.frequency_value}
              onChange={e => setCfg(prev => ({ ...prev, frequency_value: e.target.value }))}
              className="w-20 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            />
            <select
              value={cfg.frequency_unit}
              onChange={e => setCfg(prev => ({ ...prev, frequency_unit: e.target.value }))}
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="days">jour(s)</option>
              <option value="hours">heure(s)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-gray-400 text-xs">Validation avant publication</label>
          <div className="flex gap-2 mt-1">
            {[
              { id: 'manual', label: '🧐 Je valide chaque post' },
              { id: 'auto', label: '⚡ Publication 100% automatique' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setCfg(prev => ({ ...prev, validation_mode: opt.id }))}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                  cfg.validation_mode === opt.id ? 'bg-blue-600 border-blue-500 text-white' : 'bg-black/20 border-white/10 text-gray-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>


        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Enregistrer
          </button>
          <button
            onClick={generateNow}
            disabled={generating}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Générer une publication maintenant
          </button>
          {message && (
            <span className={`text-xs ${message.type === 'ok' ? 'text-green-400' : message.type === 'info' ? 'text-blue-300' : 'text-red-400'}`}>
              {message.text}
            </span>
          )}
        </div>
      </div>
      </>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-white text-sm font-medium">Publications générées</span>
          <div className="flex items-center gap-3">
            <span className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-full ${videoCount >= maxStoredVideos ? 'bg-red-500/15 text-red-300' : 'bg-white/5 text-gray-400'}`}>
              <Video className="w-3 h-3" /> {videoCount}/{maxStoredVideos} vidéos
            </span>
            <button onClick={loadPosts} className="text-gray-400 hover:text-white"><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {posts.length > 0 && (
          <div className="flex items-start gap-2 text-xs bg-blue-500/10 text-blue-300 rounded-lg p-2.5 mb-2">
            <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Vos photos et vidéos ne sont conservées que {MEDIA_RETENTION_DAYS} jours sur nos serveurs IA, puis supprimées automatiquement. Téléchargez rapidement celles que vous voulez garder.</span>
          </div>
        )}

        {videoCount >= maxStoredVideos && (
          <div className="flex items-start gap-2 text-xs bg-red-500/10 text-red-300 rounded-lg p-2.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Limite de {maxStoredVideos} vidéos atteinte. Supprimez-en une (définitivement) pour en générer une nouvelle.</span>
          </div>
        )}

        {posts.length === 0 ? (
          <p className="text-gray-500 text-xs text-center py-4">Aucune publication générée pour l'instant.</p>
        ) : (
          <div className="space-y-3">
            {posts.map(p => {
              const statusInfo = STATUS_LABELS[p.status] || { label: p.status, color: 'text-gray-400 bg-gray-500/10' };
              return (
                <div key={p.id} className="bg-black/20 rounded-xl p-3 flex gap-3">
                  {p.image_url && p.media_kind === 'video' ? (
                    <video
                      src={p.image_url} controls
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0 [&:fullscreen]:object-contain [&:fullscreen]:w-full [&:fullscreen]:h-full [&:-webkit-full-screen]:object-contain [&:-webkit-full-screen]:w-full [&:-webkit-full-screen]:h-full"
                    />
                  ) : p.image_url ? (
                    <img src={p.image_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                  ) : null}
                  {!p.image_url && p.status === 'generating' && (
                    <div className="w-20 h-20 rounded-lg bg-black/30 flex items-center justify-center flex-shrink-0">
                      {p.media_kind === 'video' ? <Video className="w-6 h-6 text-gray-600" /> : <ImageIcon className="w-6 h-6 text-gray-600" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.color}`}>{statusInfo.label}</span>
                        <span className="text-gray-500 text-[10px] flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(p.created_at).toLocaleString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {p.image_url && (
                          <a
                            href={p.image_url}
                            download={`teckia_${p.media_kind === 'video' ? 'video' : 'image'}_${p.id}.${p.media_kind === 'video' ? 'mp4' : 'jpg'}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Télécharger"
                            className="text-gray-500 hover:text-blue-300"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => deletePost(p.id)}
                          disabled={actingOn === p.id}
                          title="Supprimer définitivement"
                          className="text-gray-500 hover:text-red-400 disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {p.image_url && daysRemaining(p.created_at) !== null && (
                      <div className="text-gray-600 text-[10px] mb-1">
                        {daysRemaining(p.created_at) > 0
                          ? `Expire dans ${daysRemaining(p.created_at)} jour${daysRemaining(p.created_at) > 1 ? 's' : ''}`
                          : 'Expire bientôt'}
                      </div>
                    )}
                    {p.status === 'draft_review' ? (
                      <div className="space-y-1.5">
                        <div>
                          <label className="text-gray-500 text-[10px]">Légende</label>
                          <textarea
                            value={editCaptions[p.id] ?? p.caption}
                            onChange={e => setEditCaptions(prev => ({ ...prev, [p.id]: e.target.value }))}
                            rows={2}
                            className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-[10px]">Ce que TeckiA CM Studio va générer visuellement (vérifiez/corrigez)</label>
                          <textarea
                            value={editImagePrompts[p.id] ?? p.image_prompt}
                            onChange={e => setEditImagePrompts(prev => ({ ...prev, [p.id]: e.target.value }))}
                            rows={2}
                            className="w-full bg-black/30 border border-purple-500/20 rounded-lg px-2 py-1.5 text-white text-xs"
                          />
                        </div>
                        {p.video_script && (
                          <div>
                            <label className="text-yellow-400 text-[10px]">Texte exact dit par la personne (UGC) — vérifiez la langue et le sens</label>
                            <textarea
                              value={editScripts[p.id] ?? p.video_script}
                              onChange={e => setEditScripts(prev => ({ ...prev, [p.id]: e.target.value }))}
                              rows={2}
                              className="w-full bg-black/30 border border-yellow-500/30 rounded-lg px-2 py-1.5 text-white text-xs"
                            />
                          </div>
                        )}
                      </div>
                    ) : p.status === 'pending_validation' ? (
                      <textarea
                        value={editCaptions[p.id] ?? p.caption}
                        onChange={e => setEditCaptions(prev => ({ ...prev, [p.id]: e.target.value }))}
                        rows={2}
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
                      />
                    ) : (
                      <p className="text-gray-300 text-xs whitespace-pre-wrap">{p.caption}</p>
                    )}
                    {p.error_message && <p className="text-red-400 text-[10px] mt-1">{p.error_message}</p>}
                    {p.status === 'draft_review' && (
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => confirmDraft(p.id)}
                          disabled={actingOn === p.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-[11px] font-medium disabled:opacity-50"
                        >
                          {actingOn === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Lancer la génération
                        </button>
                        <button
                          onClick={() => reject(p.id)}
                          disabled={actingOn === p.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-[11px] font-medium disabled:opacity-50"
                        >
                          <XCircle className="w-3 h-3" /> Rejeter
                        </button>
                      </div>
                    )}
                    {p.status === 'pending_validation' && (
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => approve(p.id)}
                          disabled={actingOn === p.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-[11px] font-medium disabled:opacity-50"
                        >
                          {actingOn === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Publier
                        </button>
                        <button
                          onClick={() => reject(p.id)}
                          disabled={actingOn === p.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-[11px] font-medium disabled:opacity-50"
                        >
                          <XCircle className="w-3 h-3" /> Rejeter
                        </button>
                      </div>
                    )}
                    {p.status === 'published' && (
                      <span className="text-green-400 text-[11px] flex items-center gap-1 mt-1"><CheckCircle className="w-3 h-3" /> Publié sur la Page</span>
                    )}
                    {p.status === 'scheduled' && p.scheduled_at && (
                      <span className="text-blue-300 text-[11px] flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3" /> Publication prévue le {new Date(p.scheduled_at).toLocaleString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
